/**
 * 租户配置文件
 * 定义所有租户的配置信息
 */
import defaultLogo from '@/assets/logo.png'
import luatosLogo from '@/assets/fav1.png'
/**
 * 租户配置接口（不包含 id，id 由 key 自动生成）
 */
export interface TenantConfig {
  /** 租户名称 */
  name: string
  /** 租户域名 */
  domain: string
  /** API 基础地址 */
  apiBaseUrl?: string
  /** IAM 服务路径 */
  iamPath?: string
  /** Logo 路径 */
  logo?: string
  /** 版权信息 */
  copyright?: string
  /** 主题配置 */
  theme?: {
    /** 主色调 */
    primaryColor: string
    /** 背景色 */
    backgroundColor: string
    /** 渐变色（可选） */
    gradientColors?: string[]
  }
  /** 功能开关 */
  features: {
    /** 是否支持注册 */
    register: boolean
    /** 是否支持忘记密码 */
    forgotPassword: boolean
    /** 是否支持社交登录 */
    socialLogin: boolean
    /** 是否支持多租户切换 */
    multiTenant: boolean
    /** 是否支持短信登录 */
    smsLogin: boolean
  }
  /** 额外配置（可选） */
  extra?: Record<string, any>
}

/**
 * 完整的租户配置接口（包含自动生成的 id）
 */
export interface TenantConfigWithId extends TenantConfig {
  /** 租户唯一标识（自动从 key 生成） */
  id: string
}

/**
 * 租户基础配置
 */
const TENANT_CONFIGS: Record<string, TenantConfig> = {
  // 默认租户
  default: {
    name: 'D3INF',
    domain: 'www.d3inf.com',
    logo: defaultLogo,
    copyright: '© 2024 D3INF. All rights reserved.',
    // theme: {
    //   primaryColor: '#0e61b4',
    //   backgroundColor: '#f5f7fa',
    //   gradientColors: ['#398cfe', '#1a6fdc']
    // },
    features: {
      register: true,
      forgotPassword: true,
      socialLogin: true,
      multiTenant: true,
      smsLogin: true
    },
    // extra: {
    //   supportPhone: '400-123-4567',
    //   icp: '京ICP备12345678号'
    // }
  },

  // Luat 租户
  luatos: {
    name: 'Luat',
    domain: 'luatos.com',
    logo: luatosLogo,
    copyright: '© 2024 Luat. All rights reserved.',
    // theme: {
    //   primaryColor: '#1890ff',
    //   backgroundColor: '#ffffff',
    //   gradientColors: ['#667eea', '#764ba2']
    // },
    features: {
      register: true,
      forgotPassword: true,
      socialLogin: false,
      multiTenant: true,
      smsLogin: false
    },
    // extra: {
    //   supportEmail: 'support@luatos.com'
    // }
  }
}

/**
 * 租户页面组件映射
 * key 必须与 TENANT_CONFIGS 中的 key 一致
 */
type PageLoader = () => Promise<any>
const TENANT_PAGES: Record<string, { login: PageLoader; register: PageLoader; forgotPassword: PageLoader }> = {
  default: {
    login: () => import('@/views/tenants/default/index.vue'),
    register: () => import('@/views/tenants/default/register/index.vue'),
    forgotPassword: () => import('@/views/tenants/default/forgot-password/index.vue'),
  },
  luatos: {
    login: () => import('@/views/tenants/luatos/index.vue'),
    register: () => import('@/views/tenants/luatos/register/index.vue'),
    forgotPassword: () => import('@/views/tenants/luatos/forgot-password/index.vue'),
  },
}

/**
 * 自动生成完整的租户配置（包含 id）
 */
export const TENANTS: Record<string, TenantConfigWithId> = Object.keys(TENANT_CONFIGS).reduce((acc, key) => {
  acc[key] = {
    ...TENANT_CONFIGS[key],
    id: key
  }
  return acc
}, {} as Record<string, TenantConfigWithId>)

/**
 * 导出页面组件映射（保持向后兼容）
 */
export const tenantPages = TENANT_PAGES

/**
 * 根据租户ID获取对应页面组件，找不到则回退 default
 */
export function getTenantComponent(tenantId: string, page: keyof typeof TENANT_PAGES['luatos']) {
  return (TENANT_PAGES[tenantId]?.[page] || TENANT_PAGES.default[page])()
}



/**
 * 默认租户 ID
 */
export const DEFAULT_TENANT_ID = ('VITE_PLATFORM' in import.meta.env) ? 'luatos' : 'luatos'


/**
 * 根据 ID 获取租户配置
 * @param tenantId 租户 ID
 * @returns 租户配置，如果未找到则返回 undefined
 */
export function getTenantById(tenantId: string): TenantConfigWithId | undefined {
  return TENANTS[tenantId]
}

/**
 * 获取默认租户配置
 * @returns 默认租户配置
 */
export function getDefaultTenant(): TenantConfigWithId {
  return TENANTS[DEFAULT_TENANT_ID]
}


/**
 * 1.首先针对特殊类型合宙单独登录体系合宙
 * 2.其余项目所属通用登录
 * APP 小程序（h5）
 */